import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileUp, Send, RefreshCw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { DateField } from "@/components/common/DateField";
import { FilePicker } from "@/components/submittals/FilePicker";
import { SUBMITTAL_TYPES } from "@/components/submittals/submittalMeta";

/**
 * Phase 136 — lodge a submittal (or a new revision of one).
 *  mode="admin"      builder lodges on a contractor's / supplier's behalf (directory pick or free-text name + e-mail)
 *  mode="contractor" contractor / supplier lodges from the portal against an assigned job
 *  resubmitFor={submittal} → revision mode: note + files only.
 */
export const LodgeSubmittalSheet = ({ open, onOpenChange, mode = "admin", jobs = [], defaultJobId = "", options = null, resubmitFor = null, onSaved }) => {
  const resubmit = !!resubmitFor;
  const [jobId, setJobId] = useState("");
  const [type, setType] = useState("shop_drawings");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [specRef, setSpecRef] = useState("");
  const [requiredBy, setRequiredBy] = useState("");
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [fromKind, setFromKind] = useState("contractor"); // contractor | supplier | other
  const [fromId, setFromId] = useState("");
  const [fromName, setFromName] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [fromCompany, setFromCompany] = useState("");
  const [busy, setBusy] = useState(false);
  const [opts, setOpts] = useState(options);

  useEffect(() => {
    if (!open) return;
    setJobId(defaultJobId || (jobs.length === 1 ? jobs[0].id : ""));
    setType("shop_drawings");
    setTitle("");
    setDescription("");
    setSpecRef("");
    setRequiredBy("");
    setNote("");
    setFiles([]);
    setFromKind("contractor");
    setFromId("");
    setFromName("");
    setFromEmail("");
    setFromCompany("");
  }, [open, defaultJobId, jobs]);

  useEffect(() => {
    if (!open || mode !== "admin" || options) return;
    api.get("/submittals/options").then((r) => setOpts(r.data)).catch(() => {});
  }, [open, mode, options]);
  useEffect(() => { if (options) setOpts(options); }, [options]);

  const submit = async () => {
    if (!resubmit) {
      if (!jobId) return toast.error("Pick the job this submittal is for");
      if (!title.trim()) return toast.error("Give the submittal a title");
      if (!files.length && !description.trim()) return toast.error("Attach at least one file or describe the submission");
      if (mode === "admin" && fromKind !== "other" && !fromId) return toast.error(`Pick the ${fromKind} this submittal is from`);
      if (mode === "admin" && fromKind === "other" && !fromName.trim() && !fromEmail.trim()) return toast.error("Who is this submittal from? Add a name or e-mail");
    } else if (!files.length && !note.trim()) {
      return toast.error("Attach the revised files or add a note");
    }
    const fd = new FormData();
    if (resubmit) {
      fd.append("note", note.trim());
    } else {
      fd.append("job_id", jobId);
      fd.append("type", type);
      fd.append("title", title.trim());
      fd.append("description", description.trim());
      fd.append("spec_ref", specRef.trim());
      fd.append("note", note.trim());
      if (mode === "admin") {
        fd.append("required_by", requiredBy || "");
        fd.append("submitter_kind", fromKind === "other" ? "contractor" : fromKind);
        fd.append("submitter_id", fromKind === "other" ? "" : fromId);
        fd.append("submitter_name", fromKind === "other" ? fromName.trim() : "");
        fd.append("submitter_email", fromKind === "other" ? fromEmail.trim() : "");
        fd.append("submitter_company", fromKind === "other" ? fromCompany.trim() : "");
      }
    }
    files.forEach((f) => fd.append("files", f, f.name));
    const url = resubmit
      ? mode === "admin" ? `/submittals/${resubmitFor.id}/revisions` : `/contractor/submittals/${resubmitFor.id}/revisions`
      : mode === "admin" ? "/submittals" : "/contractor/submittals";
    setBusy(true);
    try {
      const res = await api.post(url, fd);
      const d = res.data;
      toast.success(resubmit ? `${d.number} — revision ${d.current_revision} lodged, back under review` : `${d.number} lodged — pending review`);
      onSaved?.(d);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const contractors = opts?.contractors || [];
  const suppliers = opts?.suppliers || [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[94dvh] overflow-y-auto rounded-t-2xl border-border bg-card px-4 pb-8" data-testid="lodge-submittal-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center gap-2 text-base">
            {resubmit ? <RefreshCw className="h-4 w-4 text-primary" /> : <FileUp className="h-4 w-4 text-primary" />}
            {resubmit ? `Resubmit ${resubmitFor.number} — revision ${(resubmitFor.current_revision || 1) + 1}` : mode === "admin" ? "Log a submittal on a contractor's behalf" : "Lodge a submittal"}
          </SheetTitle>
          <SheetDescription className="text-xs">
            {resubmit
              ? "Attach the revised files and say what changed. The previous revision and its review stay on record."
              : mode === "admin"
                ? "It arrived by e-mail or over the counter — file it here so the review and revision history live on the job."
                : "Shop drawings, product data, samples, SWMS or certificates for the builder to review before the work proceeds."}
          </SheetDescription>
        </SheetHeader>

        <div className="mx-auto mt-4 max-w-3xl space-y-4">
          {resubmit && (
            <div className="rounded-lg border border-border bg-muted/20 p-3 text-sm" data-testid="lodge-submittal-resubmit-context">
              <p className="font-semibold">{resubmitFor.title}</p>
              {resubmitFor.last_review?.comments && (
                <p className="mt-1 whitespace-pre-wrap text-muted-foreground"><span className="font-semibold text-amber-300">Reviewer asked:</span> {resubmitFor.last_review.comments}</p>
              )}
            </div>
          )}

          {!resubmit && (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Job</Label>
                  <Select value={jobId} onValueChange={setJobId}>
                    <SelectTrigger className="h-11" data-testid="lodge-submittal-job-select">
                      <SelectValue placeholder="Which job?" />
                    </SelectTrigger>
                    <SelectContent>
                      {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Type</Label>
                  <Select value={type} onValueChange={setType}>
                    <SelectTrigger className="h-11" data-testid="lodge-submittal-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SUBMITTAL_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-[2fr_1fr]">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Title</Label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Kitchen joinery shop drawings" className="h-11" data-testid="lodge-submittal-title-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Spec / drawing ref</Label>
                  <Input value={specRef} onChange={(e) => setSpecRef(e.target.value)} placeholder="e.g. Spec 06.4 · A-501" className="h-11" data-testid="lodge-submittal-spec-input" />
                </div>
              </div>

              {mode === "admin" && (
                <div className="space-y-3 rounded-xl border border-border bg-background p-3" data-testid="lodge-submittal-from">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">Submitted by</p>
                    <ToggleGroup type="single" value={fromKind} onValueChange={(v) => { if (v) { setFromKind(v); setFromId(""); } }} className="gap-1" data-testid="lodge-submittal-from-kind">
                      <ToggleGroupItem value="contractor" className="h-8 border border-border px-3 text-xs data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary" data-testid="lodge-submittal-from-contractor">Contractor</ToggleGroupItem>
                      <ToggleGroupItem value="supplier" className="h-8 border border-border px-3 text-xs data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary" data-testid="lodge-submittal-from-supplier">Supplier</ToggleGroupItem>
                      <ToggleGroupItem value="other" className="h-8 border border-border px-3 text-xs data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary" data-testid="lodge-submittal-from-other">Someone else</ToggleGroupItem>
                    </ToggleGroup>
                  </div>
                  {fromKind === "other" ? (
                    <div className="grid gap-2 sm:grid-cols-3">
                      <Input value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Name" className="h-10" data-testid="lodge-submittal-from-name" />
                      <Input value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} placeholder="email@example.com (for the outcome e-mail)" type="email" className="h-10" data-testid="lodge-submittal-from-email" />
                      <Input value={fromCompany} onChange={(e) => setFromCompany(e.target.value)} placeholder="Company" className="h-10" data-testid="lodge-submittal-from-company" />
                    </div>
                  ) : (
                    <Select value={fromId} onValueChange={setFromId}>
                      <SelectTrigger className="h-10" data-testid="lodge-submittal-from-select">
                        <SelectValue placeholder={fromKind === "contractor" ? "Pick the contractor…" : "Pick the supplier…"} />
                      </SelectTrigger>
                      <SelectContent>
                        {(fromKind === "contractor" ? contractors : suppliers).map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}{fromKind === "contractor" ? (c.trade ? ` · ${c.trade}` : "") : (c.contact_name ? ` · ${c.contact_name}` : "")}{c.email ? "" : " · no email"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Review needed by (optional)</Label>
                    <DateField value={requiredBy} onChange={setRequiredBy} placeholder="Pick a date" clearable testId="lodge-submittal-required-by" />
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Description</Label>
                <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="What is being submitted and what you need confirmed…" className="text-sm" data-testid="lodge-submittal-description-input" />
              </div>
            </>
          )}

          {resubmit && (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.14em] text-muted-foreground">What changed in this revision</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="e.g. Island overhang updated to 300mm on sheet 3; handle finish confirmed as brushed nickel." className="text-sm" data-testid="lodge-submittal-note-input" />
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.14em] text-muted-foreground">{resubmit ? "Revised files" : "Files"}</Label>
            <FilePicker files={files} setFiles={setFiles} testPrefix="lodge-submittal-files" label={resubmit ? "Add the revised files" : "Add files"} />
          </div>

          <Button className="h-12 w-full text-sm font-semibold" onClick={submit} disabled={busy} data-testid="lodge-submittal-submit-btn">
            <Send className="mr-2 h-4 w-4" />
            {busy ? "Uploading…" : resubmit ? "Lodge revision" : "Lodge submittal"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
